// The ONLY transport layer. Bearer + refresh-on-401 (sliding session). Features call req() via their
// TanStack Query hooks. Base path is proxied by Vite to api2 (:8312).
import { auth, tryRefresh, ensureFresh } from "../auth.js";
import { requestStepUpToken, peekStepUpToken, rememberRouteScope, knownRouteScope } from "../auth/step-up-controller.js";
import { parseInsufficientScope } from "./step-up-challenge.js";

const BASE = (import.meta.env.VITE_API_BASE as string | undefined) ?? "/giftcard/api";

/** Resolve an api-relative file path (e.g. a stored logo) to a full URL; passes http(s) through. */
export const fileUrl = (p?: string | null): string | undefined => (p ? (/^https?:/.test(p) ? p : BASE + p) : undefined);

const authHeader = (): Record<string, string> => (auth.token() ? { Authorization: `Bearer ${auth.token()}` } : {});

export async function req<T>(path: string, opts: RequestInit = {}, _retried = false, _stepUp = false): Promise<T> {
  if (!_retried) await ensureFresh(); // rotate a stale token BEFORE sending, so the first call doesn't 401

  // Pre-attach an elevated token we ALREADY hold for an endpoint the server has ALREADY told us is
  // sensitive. Without this, every sensitive call inside the step-up TTL still burns a doomed 403 just to
  // rediscover a scope we know and a token we're holding. We never guess: `knownRouteScope` is only ever
  // populated from a real 403 challenge, and if the guess goes stale the request just 403s and falls into
  // the normal challenge path below. `_stepUp` marks the request as already-elevated so a 403 on THIS
  // token is a genuine denial, not a re-prompt loop.
  const routeKey = `${opts.method ?? "GET"} ${path}`;
  let elevated: string | null = null;
  if (!_stepUp) {
    const known = knownRouteScope(routeKey);
    if (known) elevated = peekStepUpToken(known); // null ⇒ no live token ⇒ send unelevated, get challenged
  }

  const res = await fetch(BASE + path, {
    ...opts,
    headers: {
      "Content-Type": "application/json",
      ...authHeader(),
      ...(elevated ? { Authorization: `Bearer ${elevated}` } : {}), // the elevated token WINS over the session one
      ...(opts.headers as Record<string, string> | undefined),
    },
  });
  const text = await res.text();
  const data = text ? JSON.parse(text) : null;
  if (res.status === 401) { // token expired → rotate + retry once, else drop to login
    if (!_retried && (await tryRefresh())) return req<T>(path, opts, true, _stepUp);
    auth.clear(); location.reload();
  }
  // A 403 still challenges us UNLESS this request was already the post-OTP replay (`_stepUp`). A 403 on a
  // token we merely PRE-ATTACHED is recoverable — the mapping or the token went stale — so it falls
  // through here and re-challenges normally (requestStepUpToken re-prompts if the cache is now empty).
  if (res.status === 403 && !_stepUp) { // RFC 9470: sensitive action → OTP step-up → replay once
    const scope = parseInsufficientScope(data);
    if (scope) {
      rememberRouteScope(routeKey, scope);          // learn it, so the NEXT call can skip this 403
      const token = await requestStepUpToken(scope); // cached token, or opens the OTP modal; throws if cancelled
      return req<T>(path, { ...opts, headers: { ...(opts.headers as Record<string, string> | undefined), Authorization: `Bearer ${token}` } }, _retried, true);
    }
  }
  if (!res.ok) throw apiError(res.status, data);
  return data as T;
}

/** Multipart upload (no JSON content-type — the browser sets the boundary). Bearer auth. */
export async function upload<T>(path: string, form: FormData): Promise<T> {
  await ensureFresh();
  const res = await fetch(BASE + path, { method: "POST", body: form, headers: auth.token() ? { Authorization: `Bearer ${auth.token()}` } : {} });
  const text = await res.text();
  const data = text ? JSON.parse(text) : null;
  if (!res.ok) throw apiError(res.status, data);
  return data as T;
}

/** Authed STREAMING GET (e.g. an SSE endpoint). Returns the raw Response so the caller can read
 *  res.body itself — the browser's native EventSource cannot send an Authorization header, so a
 *  Bearer-guarded event stream has to be read this way. */
export async function stream(path: string, opts: RequestInit = {}): Promise<Response> {
  await ensureFresh();
  return fetch(BASE + path, { ...opts, headers: { ...authHeader(), ...(opts.headers ?? {}) } });
}

/** Authed file download (e.g. CSV export) → fetches a blob with Bearer auth and triggers a browser save. */
export async function download(path: string, filename: string): Promise<void> {
  await ensureFresh();
  const res = await fetch(BASE + path, { headers: authHeader() });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const url = URL.createObjectURL(await res.blob());
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

/** An API failure that keeps the server's structured `details` instead of flattening it to a string.
 *  A 400 from api2's `parse()` carries zod's `flatten()` there ({ formErrors, fieldErrors }) — the only
 *  thing that tells a form WHICH field the server rejected. This used to be dropped, leaving every
 *  caller with nothing but the generic catalog message ("request failed validation").
 *  `message` keeps its previous value, so existing `(e as Error).message` call sites are unaffected. */
export class ApiError extends Error {
  constructor(message: string, readonly status: number, readonly eventId?: string, readonly details?: unknown) {
    super(message);
    this.name = "ApiError";
  }
}

function apiError(status: number, data: unknown): ApiError {
  const d = (data ?? {}) as { error?: unknown; eventId?: unknown; details?: unknown };
  return new ApiError(d.error ? String(d.error) : `HTTP ${status}`, status,
    d.eventId ? String(d.eventId) : undefined, d.details);
}
